import React, { useEffect } from "react";
import { CustomModal } from "@/components/Modal";
import { Box, Button, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { CAutocompleteMultiFreeSoloTextField } from "@/components/TextField/CAutocompleteMultiFreeSoloTextField";
import { SizingWrapperStyle } from "@/components/Wrapper";

interface TagFormData {
  tags: string[];
}

interface MapPlotRequestTagModalProps {
  open: boolean;
  onClose: () => void;
  rowData: {
    id: number;
    realEstateType?: "土地" | "建物" | "区分建物";
    requestAddress: string | null;
    requestLocation: string | null;
    requestChiban: string | null;
    requestKaokuNumber?: string | null;
    tags?: Array<{ id: number; name: string }>;
  } | null;
  onSave: (id: number, tags: string[]) => void;
}

export const MapPlotRequestTagModal: React.FC<MapPlotRequestTagModalProps> = ({
  open,
  onClose,
  rowData,
  onSave,
}) => {
  const { control, getValues, reset, handleSubmit } = useForm<TagFormData>({
    defaultValues: {
      tags: [],
    },
  });

  useEffect(() => {
    if (rowData) {
      const tagNames = rowData.tags?.map((tag) => tag.name) ?? [];
      reset({ tags: tagNames });
    }
  }, [rowData, reset]);

  const onSubmit = (data: TagFormData): void => {
    if (rowData) {
      onSave(rowData.id, data.tags);
      onClose();
    }
  };

  const handleClose = (
    _: Record<string, unknown>,
    reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    if (reason === "backdropClick") {
      return;
    }
    onClose();
  };

  if (!rowData) {
    return null;
  }

  // NOTE: realEstateTypeがundefinedの場合はデフォルトで土地として扱う
  const bookType = rowData.realEstateType ?? "土地";
  const isBuilding = bookType === "建物" || bookType === "区分建物";
  const chibanLabel = isBuilding ? "家屋番号" : "地番";
  const chibanOrKaokuNumber: string | null = isBuilding
    ? rowData.requestKaokuNumber ?? null
    : rowData.requestChiban;

  return (
    <CustomModal
      isOpen={open}
      handleClose={handleClose}
      ariaLabelledby="tag-detail-modal-title"
      ariaDescribedby="tag-detail-modal-description"
      width={600}
      overflow="auto"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit(onSubmit)(e);
        }}
      >
        <Stack spacing={3}>
          <Typography id="tag-detail-modal-title" variant="h5" component="h2">
            タグ詳細
          </Typography>

          <Box>
            <Typography variant="body2" color="text.secondary">
              住居表示: {rowData.requestAddress ?? "-"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              所在: {rowData.requestLocation ?? "-"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {chibanLabel}: {chibanOrKaokuNumber ?? "-"}
            </Typography>
          </Box>

          <CAutocompleteMultiFreeSoloTextField
            control={control}
            name="tags"
            label="タグ"
            size={SizingWrapperStyle.LARGE}
            maxLength={20}
          />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={onClose} size="large">
              キャンセル
            </Button>
            <Button variant="contained" type="submit" size="large">
              保存
            </Button>
          </Stack>
        </Stack>
      </form>
    </CustomModal>
  );
};
